import React, { useEffect, useState } from 'react';
import { Download, Search, Filter, TrendingUp, TrendingDown, FileText, ArrowDownLeft, RefreshCcw } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import { Helmet } from 'react-helmet-async';
import { Panigation } from '@/components/ui/Panigation';
import WithdrawalRequestModal from './WithdrawalRequestModal';
import { fetchTransactions } from '@/api/Transaction';
import { TransactionListAPI } from '@/api/type';
import { formatCurrency } from '@/lib/commonConvert';
import dayjs from 'dayjs';

const breadcrumbs: BreadcrumbItem[] = [
  { label: 'Dashboard', path: '/home' },
  { label: 'Nhật ký Giao dịch' },
];

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionListAPI>();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);


  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'investment':
        return <Download className="w-4 h-4 text-purple-600" />;
      case 'profit':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'refund':
        return <TrendingDown className="w-4 h-4 text-amber-600" />;
      case 'commission':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'withdrawal':
        return <ArrowDownLeft className="w-4 h-4 text-red-600" />;
      case 'transfer':
        return <RefreshCcw className="w-4 h-4 text-blue-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  useEffect(() => {
    setLoading(true);
    setTransactions(undefined);
    fetchTransactions(currentPage, itemsPerPage, typeFilter).then(data => {
      setTransactions(data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [currentPage, typeFilter]);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Lịch Sử Giao dịch - BeQ Rewards</title>
      </Helmet>

      <PageHeader title="Lịch Sử Giao dịch" breadcrumbs={breadcrumbs} />

      {/* Button and Summary Cards */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Tổng quan giao dịch
        </h2>
        {/* <button
          onClick={() => setShowWithdrawalModal(true)}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dark !text-white px-6 py-3 rounded-lg transition-colors border-none cursor-pointer"
        >
          <Download className="w-5 h-5" />
          Yêu cầu rút tiền
        </button> */}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-600 dark:text-gray-400 mb-1">
                Tổng tiền vào
              </p>
              <p className="md:text-2xl text-lg font-bold text-green-600 !mb-0">
                {transactions ? formatCurrency(transactions.summary.total_in, 'đ') : '0 đ'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-600 dark:text-gray-400 mb-1">
                Tổng tiền ra
              </p>
              <p className="md:text-2xl text-lg font-bold text-red-600 !mb-0">
                {transactions ? formatCurrency(transactions.summary.total_out, 'đ') : '0 đ'}
              </p>
            </div>
            <TrendingDown className="w-6 h-6 text-red-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-gray-600 dark:text-gray-400 mb-1">
                Tổng giao dịch
              </p>
              <p className="md:text-2xl text-lg font-bold text-gray-900 dark:text-white !mb-0">
                {transactions ? transactions.data.total : 0} giao dịch
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        {/* Search Bar */}
        <div className="flex gap-4 ">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm theo mã giao dịch, mô tả, đối tác..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:!text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Bộ lọc
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Loại giao dịch
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tất cả</option>
                <option value="investment">Đầu tư gói</option>
                <option value="commission">Hoa hồng</option>
                <option value="profit">Lợi nhuận</option>
                <option value="refund">Hoàn tiền</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trạng thái
              </label>
              <select
                disabled
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tất cả</option>
                <option value="success">Thành công</option>
                <option value="pending">Đang xử lý</option>
                <option value="failed">Thất bại</option>
              </select>
            </div>

            {/* Time Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Thời gian
              </label>
              <select
                disabled
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tất cả</option>
                <option value="today">Hôm nay</option>
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
                <option value="year">Năm này</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Mã GD
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Thời gian
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Loại giao dịch
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Số tiền
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {transactions?.data.data.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getTransactionIcon(transaction.transaction_type)}
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {transaction.payment_code}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {new Date(transaction.created_at).toLocaleDateString('vi-VN')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {dayjs(transaction.created_at).format('HH:mm:ss')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {transaction.transaction_type == "investment" ? "Đầu tư gói" : null}
                      {transaction.transaction_type == "commission" ? "Hoa hồng" : null}
                      {transaction.transaction_type == "profit" ? "Lợi nhuận" : null}
                      {transaction.transaction_type == "refund" ? "Hoàn tiền" : null}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 max-w-md">
                      {transaction.description } </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span
                      className={`text-sm font-bold dark:text-white ${transaction.transaction_type === 'transfer' ? 'text-red-600' : 'text-green-600'}`}
                    >
                      {(transaction.status == 'pending') ? 'Đang xử lý' : (transaction.transaction_type === 'transfer') ? `-${formatCurrency(Math.abs(Number(transaction.amount)), 'đ')}` : `${formatCurrency(Math.abs(Number(transaction.amount)), 'đ')}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${transaction.status === 'succeed'
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                      : transaction.status === 'pending'
                        ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'}`}>
                      {transaction.status === 'succeed'
                        ? 'Thành công'
                        : transaction.status === 'pending'
                          ? 'Đang xử lý'
                          : 'Thất bại'}
                    </span>
                  </td>
                </tr>
              ))}
              {loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Đang tải dữ liệu...</p>
                  </td>
                </tr>
              )}
              {!loading && transactions?.data.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Không có giao dịch nào.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Panigation
          currentPage={currentPage}
          totalPages={transactions ? transactions.data.last_page : 1}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Withdrawal Request Modal */}
      {showWithdrawalModal && (
        <WithdrawalRequestModal
          onClose={() => setShowWithdrawalModal(false)}
          availableBalance={500000000}
        />
      )}
    </div>
  );
};

export default TransactionHistory;
